
export const TYPE = 'tick-mark';

/**
 * @property {number} top     The top value of the tick mark.
 * @property {string} label   The attachment point label.
 */
export interface TickMarkOptions extends fabric.Group {
  objects: fabric.Object[];
  lineOptions: fabric.ILineOptions;
  textOptions: fabric.ITextOptions;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface TickMark extends TickMarkOptions {}
/**
 * TickMark is a custom fabric object that represents the attachment points
 * of vertically stacked policies that represent start/stop points of policy premium coverage.
 * @extends fabric.Group
 *
 * @property {string} type  The fabric object's type.
 */
const TickMark = fabric.util.createClass(fabric.Group, {
  type: TYPE,

  initialize: function (options: TickMarkOptions) {
    const defaults: TickMarkOptions = {
      id: `${TYPE}-${options.top}`,
      originY: 'center',
    } as TickMarkOptions;

    const tickMark = createTickMarkLine(options.lineOptions);
    const tickLabel = createTickMarkLabel(options.textOptions);
    this.callSuper('initialize', [tickMark, tickLabel], { ...defaults, ...options });
  },

  toObject: function () {
    return fabric.util.object.extend(this.callSuper('toObject'), {
      id: this.id,
      lineOptions: this.lineOptions,
      textOptions: this.textOptions,
    });
  },
});

TickMark.fromObject = function (object: fabric.Object, callback: (obj: fabric.Object) => void) {
  const tickMark = new TickMark(object);
  if (callback) callback(tickMark);
  return tickMark;
};

export default TickMark;

/**
 * Create the Tower Scale's tick mark object.
 * @param opts
 */
function createTickMarkLine(opts: fabric.ILineOptions): fabric.Line {
  const defaults: fabric.ILineOptions = {
    originX: 'center',
    fill: 'black',
    stroke: 'black',
    strokeWidth: 2,
    opacity: 0.3,
  } as fabric.ILineOptions;

  const points = opts ? [opts.x1 ?? -10, opts.y1 ?? 0, opts.x2 ?? 0, opts.y2 ?? 0] : [-10, 0, 0, 0];
  return new fabric.Line(points, { ...defaults, ...opts });
}

function createTickMarkLabel(opts: fabric.ITextOptions): fabric.IText {
  const defaults: fabric.ITextOptions = {
    originX: 'center',
    originY: 'center',
    fill: 'black',
    stroke: 'black',
    strokeWidth: 0.8,
    opacity: 0.3,
  } as fabric.ITextOptions;
  return new fabric.IText(opts.text ?? '', { ...defaults, ...opts });
}

/**
 * TickMarkOptionsBuilder is a builder class for creating TickMark objects.
 * @property {ILineOptions} lineOptions   The line options for the tick mark.
 * @property {ITextOptions} textOptions   The text options for the tick mark.
 * @method withLineOptions(opts: ILineOptions): TickMarkOptionsBuilder
 * @method withTextOptions(opts: ITextOptions): TickMarkOptionsBuilder
 * @method build(): TickMark
 */
export class TickMarkOptionsBuilder {
  private tickMarkOptions: fabric.IGroupOptions = {
    top: 0,
  }

  private lineOptions: fabric.ILineOptions = {
    fill: 'black',
    stroke: 'black',
    strokeWidth: 2,
    opacity: 0.3,
  } as fabric.ILineOptions;

  private textOptions: fabric.ITextOptions = {
    left: -55,
    fill: 'black',
    fontSize: 26,
    fontWeight: 'normal',
    fontFamily: 'Roboto',
    opacity: 0.7,
  } as fabric.ITextOptions;

  constructor(opts?: TickMarkOptions) {
    if (!opts) return;

    this.tickMarkOptions = opts;
    this.lineOptions = opts.objects.filter((o) => o.type === 'line')[0] as fabric.ILineOptions;
    this.textOptions = opts.objects.filter((o) => o.type === 'i-text')[0] as fabric.ITextOptions;
  }

  withTickMarkOptions(opts: fabric.IGroupOptions): TickMarkOptionsBuilder {
    this.tickMarkOptions = { ...this.tickMarkOptions, ...opts };
    return this;
  }

  withLineOptions(opts: fabric.ILineOptions): TickMarkOptionsBuilder {
    this.lineOptions = { ...this.lineOptions, ...opts };
    return this;
  }

  withTextOptions(opts: fabric.ITextOptions): TickMarkOptionsBuilder {
    this.textOptions = { ...this.textOptions, ...opts };
    return this;
  }

  build(): TickMarkOptions {
    return {
      ...this.tickMarkOptions,
      lineOptions: this.lineOptions,
      textOptions: this.textOptions,
    } as TickMarkOptions;
  }
}
